<script setup lang="ts">
  import { UIDatepicker, UIContentWrap } from '@gohighlevel/ghl-ui'
  import { IntervalType, SCHEDULING_OPTIONS, EndType, ScheduleRRuleOptions } from '../../const'
  import { Field } from 'vee-validate'
  import { computed, onMounted, PropType, ref } from 'vue'
  import { prepareScheduleDetails } from '../../utils'
  import dayjs from 'dayjs'
  import { getSchedulingSettingsFormV2 } from './RRuleSchema'
  import { useVueI18n, i18nLocale } from '../../locales'

  const emits = defineEmits(['onScheduleUpdate'])
  const props = defineProps({
    timezone: {
      type: String,
      required: true,
    },
    rrule: {
      type: Object as PropType<ScheduleRRuleOptions>,
      required: false,
    },
    isInternal: {
      type: Boolean,
      default: false,
    },
    enableByEndType: {
      type: Boolean,
      default: true,
    },
    isForTemplate: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  })

  const endDate = ref()
  const endDatePickerBorder = ref('1px solid rgb(248 113 113)')

  const intervalTypes = computed(() => SCHEDULING_OPTIONS.INTERVAL_TYPE.filter(it => !it.internal || (it.internal && props.isInternal)))

  const { errors, validate, values, setFieldValue } = getSchedulingSettingsFormV2(props.rrule as ScheduleRRuleOptions)
  const t = useVueI18n()

  const datePickerBorderColorChange = async () => {
    if (errors.value.endDate) {
      endDatePickerBorder.value = '1px solid rgb(248 113 113)'
    } else {
      endDatePickerBorder.value = '1px solid rgb(224, 224, 230)'
    }
  }

  const onChangeValues = async () => {
    const validation = await validate()
    const data = {
      ...values,
      endType: values.endType || EndType.NEVER,
      daysBefore: values.daysBefore || 0,
    }
    const formattedSchedule = prepareScheduleDetails(data)
    datePickerBorderColorChange()
    emits('onScheduleUpdate', formattedSchedule, validation)
  }

  function allowOnlyInteger($event: any) {
    const keyCode = $event.keyCode ? $event.keyCode : $event.which
    const isNumber = keyCode >= 48 && keyCode <= 57
    const newValue = `${$event.target.value}${$event.key}`
    if (!isNumber || Number(newValue) > 99) $event.preventDefault()
  }
  const onChangeEndDate = (value: string) => {
    setFieldValue('endDate', dayjs(value).format('YYYY-MM-DD'))
    onChangeValues()
  }

  function endDateDisabled(date: Date) {
    const currentDate = dayjs().format('YYYY-MM-DD')
    const isPastDate = dayjs(date).isBefore(currentDate, 'day')
    const isBeforeStartDate = values.startDate ? dayjs(date).isBefore(dayjs(values.startDate).format('YYYY-MM-DD'), 'day') : false
    return isPastDate || isBeforeStartDate
  }

  onMounted(() => {
    if (props.rrule?.endDate) {
      endDate.value = dayjs(props.rrule?.endDate, 'YYYY-MM-DD').valueOf()
    }

    setFieldValue('useStartAsPrimaryUserSigned', true)
    onChangeValues()
  })
</script>

<template>
  <div class="py-8 mt-4">
    <label class="text-sm font-normal text-gray-500 my-2" for="intervalType">{{ t('common.setInvoiceFreq') }}</label>
    <div class="mb-2">
      <Field
        id="intervalType"
        as="select"
        name="intervalType"
        class="w-full border-gray-300 focus:outline-none focus:ring-curious-blue-500 focus:border-curious-blue-500 rounded-md text-sm p-2 border mt-2"
        :onchange="onChangeValues"
        :class="{
          'border-red-400': !!errors.intervalType,
        }"
        :disabled="props.disabled"
      >
        <option value="">{{ t('common.select') }}</option>
        <option v-for="{ value, label } in intervalTypes" :value="value" :key="value">
          {{ label }}
        </option>
      </Field>
    </div>
  </div>
  <div class="py-8 mb-8">
    <div class="w-full flex items-flex-start gap-4">
      <div class="w-4/12">
        <div class="mb-1 mt-2">
          <label class="text-sm font-normal text-gray-500" for="endType">{{ t('common.stopSending') }}</label>
        </div>
        <Field
          id="endType"
          as="select"
          name="endType"
          :onchange="onChangeValues"
          class="w-full border-gray-300 focus:outline-none focus:ring-curious-blue-500 focus:border-curious-blue-500 rounded-md text-sm p-2 border mt-2"
          :class="{
            'border-red-400': !!errors.endType,
          }"
          :disabled="props.disabled"
        >
          <option value="">{{ t('common.select') }}</option>
          <option :value="EndType.NEVER">
            {{ t('schedulingOptions.never') }}
          </option>
          <option v-if="enableByEndType" :value="EndType.BY">
            {{ t('schedulingOptions.by') }}
          </option>
          <option :value="EndType.AFTER">
            {{ t('schedulingOptions.after') }}
          </option>
        </Field>
      </div>
      <div class="w-8/12 flex flex-col">
        <template v-if="values.endType === EndType.BY">
          <div class="mb-1 mt-2">
            <label class="text-sm font-normal text-gray-500" for="by-date-picker">{{ t('common.endDate') }}</label>
          </div>
          <div>
            <UIContentWrap :locale="(i18nLocale as any) || 'en-US'" class="p-0" style="display: contents">
              <UIDatepicker
                :key="`frequency-date-picker-end-date-picker`"
                @update:value="onChangeEndDate"
                :placeholder="t('common.endDate')"
                class="fq-end-date mt-2 border-gray-300 focus:outline-none focus:ring-curious-blue-500 focus:border-curious-blue-500 rounded-md text-sm border"
                id="by-date-picker"
                v-model:value="endDate"
                ref="endDatePickerRef"
                :format="'MMMM d, yyyy'"
                :is-date-disabled="endDateDisabled"
                :input-attributes="{ readonly: true, size: 16 }"
                size="medium"
                :disabled="props.disabled"
              />
            </UIContentWrap>
            <Field type="hidden" name="endDate" />
          </div>

          <div v-if="[IntervalType.MINUTELY, IntervalType.HOURLY].includes(values.intervalType)">
            <div class="mb-1 mt-2">
              <label class="text-sm font-normal text-gray-500" for="endTime">{{ t('common.endTime') }}</label>
            </div>

            <Field
              :onchange="onChangeValues"
              id="endTime"
              type="time"
              name="endTime"
              class="w-full border-gray-300 focus:outline-none focus:ring-curious-blue-500 focus:border-curious-blue-500 rounded-md text-sm border py-2 mt-2"
              :class="{
                'border-red-400': !!errors.endTime,
              }"
              :disabled="props.disabled"
            />
          </div>
        </template>
        <template v-if="values.endType === EndType.AFTER">
          <div class="mb-1 mt-2">
            <label class="text-sm font-normal text-gray-500 capitalize" for="count">{{ t('schedulingOptions.occurrences') }}</label>
          </div>
          <Field
            :onchange="onChangeValues"
            id="count"
            as="input"
            name="count"
            class="w-20 border-gray-300 focus:outline-none focus:ring-curious-blue-500 focus:border-curious-blue-500 rounded-md text-sm p-2 border mt-2"
            :class="{
              'border-red-400': !!errors.count,
            }"
            type="number"
            @keypress="allowOnlyInteger"
            :disabled="props.disabled"
          />
        </template>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
  .n-date-picker.fq-end-date {
    .n-input {
      --n-border: v-bind('endDatePickerBorder') !important;
    }
  }
</style>
